import { AlertCircle } from 'lucide-react';
import { useId, type ReactNode } from 'react';

import { cn } from '../lib/cn';

export interface FieldProps {
  label: ReactNode;
  /** Rendered below the control when there is no error. */
  hint?: ReactNode;
  error?: string | undefined;
  required?: boolean;
  className?: string;
  /**
   * Receives the ids the control must wire up. A control that ignores these is
   * a control with no accessible name.
   */
  children: (ids: FieldIds) => ReactNode;
}

export interface FieldIds {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
}

/**
 * Label, control, hint and error, wired together.
 *
 * The wiring is the point. `aria-describedby` and `aria-invalid` are what turn
 * a red border into an error a screen-reader user can perceive, and getting
 * them right once here is better than getting them right in twenty forms.
 *
 * The error carries an icon and text as well as colour, so it survives colour
 * blindness and a monochrome print.
 */
export function Field({ label, hint, error, required = false, className, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-medium text-text">
        {label}
        {required ? (
          <span className="text-danger ms-1" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      {children({ id, describedBy, invalid: Boolean(error) })}

      {error ? (
        <p id={errorId} className="flex items-center gap-1.5 text-sm text-danger">
          <AlertCircle className="size-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-sm text-text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
